import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, Package, Tag, Users, UtensilsCrossed,
  ClipboardList, BarChart3, Settings, LogOut, Monitor,
  PanelLeftClose, PanelLeft, SlidersHorizontal, Ruler, UserCircle,
  Warehouse, Truck, Sparkles, Receipt, CalendarDays
} from "lucide-react";

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/8f834926-4714-4959-b338-a287e83dcd5c/images/09115f449cc4321ff25180a33470ced8331fabee71ffcd319e7c00cf958ac54e.png";

const navItems = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Overview", end: true },
  { to: "/dashboard/products", icon: Package, label: "Products" },
  { to: "/dashboard/categories", icon: Tag, label: "Categories" },
  { to: "/dashboard/modifiers", icon: SlidersHorizontal, label: "Modifiers" },
  { to: "/dashboard/sizes", icon: Ruler, label: "Sizes" },
  { to: "/dashboard/customers", icon: UserCircle, label: "Customers" },
  { to: "/dashboard/employees", icon: Users, label: "Employees" },
  { to: "/dashboard/scheduling", icon: CalendarDays, label: "Scheduling" },
  { to: "/dashboard/tables", icon: UtensilsCrossed, label: "Tables" },
  { to: "/dashboard/orders", icon: ClipboardList, label: "Orders" },
  { to: "/dashboard/reports", icon: BarChart3, label: "Reports" },
  { to: "/dashboard/inventory", icon: Warehouse, label: "Inventory" },
  { to: "/dashboard/vendors", icon: Truck, label: "Vendors" },
  { to: "/dashboard/specials", icon: Sparkles, label: "Specials" },
  { to: "/dashboard/service-fees", icon: Receipt, label: "Service Fees" },
  { to: "/dashboard/settings", icon: Settings, label: "Settings" },
];

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="flex min-h-screen bg-slate-50" data-testid="dashboard-layout">
      <aside className={`sidebar ${collapsed ? "collapsed" : ""} flex-shrink-0`} data-testid="dashboard-sidebar">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          {!collapsed && (
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="CashUpPOS" className="h-7 w-7" />
              <span className="font-heading text-base font-bold text-slate-900 tracking-tight">CashUpPOS</span>
            </div>
          )}
          <button onClick={() => setCollapsed(!collapsed)} className="p-1.5 rounded hover:bg-slate-100 text-slate-400" data-testid="sidebar-toggle-btn">
            {collapsed ? <PanelLeft className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
          </button>
        </div>

        <nav className="flex-1 p-3 space-y-1" data-testid="dashboard-nav">
          {navItems.map((item) => (
            <NavLink
              key={item.to} to={item.to} end={item.end}
              className={({ isActive }) => `sidebar-nav-item ${isActive ? "active" : ""}`}
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <item.icon className="h-4 w-4 flex-shrink-0" />
              {!collapsed && <span>{item.label}</span>}
            </NavLink>
          ))}
          <NavLink to="/pos" className="sidebar-nav-item mt-4 border border-dashed border-slate-300" data-testid="nav-pos">
            <Monitor className="h-4 w-4 flex-shrink-0" />
            {!collapsed && <span>Open POS</span>}
          </NavLink>
        </nav>

        <div className="p-3 border-t border-slate-100">
          {!collapsed && (
            <div className="flex items-center gap-3 px-3 py-2 mb-2">
              <div className="h-8 w-8 rounded-full bg-[var(--brand-primary)] text-white flex items-center justify-center text-xs font-bold">
                {user?.name?.charAt(0)?.toUpperCase() || "U"}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
                <p className="text-xs text-slate-400 truncate">{user?.role}</p>
              </div>
            </div>
          )}
          <button onClick={handleLogout} className="sidebar-nav-item w-full text-red-500 hover:bg-red-50 hover:text-red-600" data-testid="logout-btn">
            <LogOut className="h-4 w-4 flex-shrink-0" />
            {!collapsed && <span>Sign Out</span>}
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto" data-testid="dashboard-content">
        <Outlet />
      </main>
    </div>
  );
}
